/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TenantConfig } from './models/tenant-config.interface';

@Injectable({
  providedIn: 'root',
})
export class TenantConfigService {
  private tenantConfigsCache: TenantConfig[] | null = null;
  private cacheTimestamp: number | null = null;
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds (longer than suppliers since configs change less frequently)

  constructor(private http: HttpClient) {}
  baseUrl = `${localStorage.getItem('apiGatewayUrl')}`;

  fetch(): Observable<TenantConfig[]> {
    // Resolve API base at call time so app-open prefetch works after tenant login sets apiGatewayUrl.
    this.baseUrl = `${localStorage.getItem('apiGatewayUrl')}`;

    // Check if we have cached data that's still valid
    if (this.isCacheValid()) {
      console.log('Tenant configs fetched from cache');
      return of(this.tenantConfigsCache!);
    }

    // Cache miss or expired - fetch from API (also seeds missing Func-Settings attributes server-side).
    console.log('Tenant configs fetched from API');
    return this.http.get<TenantConfig[]>(`${this.baseUrl}/tenantConfigs`).pipe(
      tap(configs => {
        // Cache the response
        this.tenantConfigsCache = configs;
        this.cacheTimestamp = Date.now();
      })
    );
  }

  // Look up one tenant-config attribute by name. Callers interpret the value; this service
  // does not encode feature-specific keys.
  getConfigValue(configName: string): Observable<string | undefined> {
    return this.fetch().pipe(
      map((configs) => {
        const match = (configs || []).find((c) => c.configName === configName);
        return match?.configValue;
      })
    );
  }

  private isCacheValid(): boolean {
    if (!this.tenantConfigsCache || !this.cacheTimestamp) {
      return false;
    }
    
    return (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
  }

  /**
   * Clear the tenant configs cache - useful after updating configs
   */
  private clearCache(): void {
    this.tenantConfigsCache = null;
    this.cacheTimestamp = null;
    console.log('Tenant configs cache cleared');
  }

  /**
   * Manually clear the tenant configs cache - useful for forcing a refresh
   */
  public clearTenantConfigCache(): void {
    this.clearCache();
  }

  get(configId: string): Observable<TenantConfig> {
    const url = `${this.baseUrl}/tenantConfig/${configId}`;
    return this.http.get<TenantConfig>(url);
  }

  put(tenantConfig: TenantConfig) {
    const url = `${this.baseUrl}/tenantConfig/${tenantConfig.shardId}:${tenantConfig.configId}`;
    return this.http.put<TenantConfig>(url, tenantConfig).pipe(
      tap(() => this.clearCache())
    );
  }
}

